import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ShieldCheck, BookOpen, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import ShareSection from "@/components/ShareSection";
import { articles, getArticle } from "@/data/articles";

const SITE = "https://scudononni.it";
const LOGO =
  "https://customer-assets.emergentagent.com/job_40ad2524-223c-429c-8f1d-4ea2ecf9a2d3/artifacts/6n4i34v7_logo.png";

function ArticleJsonLd({ article }) {
  const data = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.description,
        datePublished: article.date,
        dateModified: article.date,
        inLanguage: "it-IT",
        image: LOGO,
        mainEntityOfPage: `${SITE}/guida/${article.slug}`,
        author: { "@type": "Organization", name: "ScudoNonni", url: SITE },
        publisher: {
          "@type": "Organization",
          name: "ScudoNonni",
          logo: { "@type": "ImageObject", url: LOGO },
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Guida Anti-Truffe", item: `${SITE}/guida` },
          { "@type": "ListItem", position: 3, name: article.title, item: `${SITE}/guida/${article.slug}` },
        ],
      },
    ],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

export default function ArticoloGuida() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <Navigate to="/guida" replace />;

  const related = (article.related || [])
    .map((s) => articles.find((a) => a.slug === s))
    .filter(Boolean);

  return (
    <article
      data-testid="articolo-guida-page"
      className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20"
    >
      <SEO
        path={`/guida/${article.slug}`}
        title={article.seoTitle}
        description={article.description}
      />
      <ArticleJsonLd article={article} />

      <nav aria-label="breadcrumb" className="mb-8">
        <Link
          to="/guida"
          data-testid="articolo-back-link"
          className="inline-flex items-center gap-2 text-lg font-bold text-[#1E3A8A] hover:text-[#152C69] transition-colors"
        >
          <ArrowLeft size={20} /> Torna alla Guida Anti-Truffe
        </Link>
      </nav>

      <header className="mb-10 animate-fade-up">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#22C55E]/15 text-[#16A34A] text-base font-bold">
            <BookOpen size={18} /> {article.category}
          </span>
          <span className="inline-flex items-center gap-2 text-base text-gray-500 font-semibold">
            <Clock size={18} /> Lettura: {article.readMinutes} minuti
          </span>
        </div>
        <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-[#1E3A8A] leading-tight">
          {article.title}
        </h1>
        <p className="mt-6 text-xl md:text-2xl text-gray-700 leading-relaxed">
          {article.intro}
        </p>
      </header>

      <div className="space-y-10">
        {article.sections.map((s) => (
          <section key={s.heading}>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-4">
              {s.heading}
            </h2>
            {s.paragraphs?.map((p) => (
              <p key={p.slice(0, 40)} className="text-lg md:text-xl text-gray-700 leading-relaxed mb-4">
                {p}
              </p>
            ))}
            {s.warningList && (
              <ul className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 space-y-3">
                {s.warningList.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-lg text-gray-800 leading-relaxed">
                    <span className="text-red-600 font-extrabold text-xl leading-none mt-1">!</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
            {s.okList && (
              <ul className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 space-y-3">
                {s.okList.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-lg text-gray-800 leading-relaxed">
                    <span className="text-green-700 font-extrabold text-xl leading-none mt-1">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <div className="mt-12 bg-[#1E3A8A] text-white rounded-[2rem] p-8 md:p-10 text-center shadow-xl">
        <ShieldCheck size={44} className="mx-auto mb-4 text-[#22C55E]" />
        <h2 className="font-heading text-2xl md:text-3xl font-extrabold mb-3">
          Hai ricevuto un messaggio simile?
        </h2>
        <p className="text-lg md:text-xl text-blue-100 mb-6 max-w-xl mx-auto">
          Incolla il testo o carica una foto: la verifica è gratuita, anonima e
          pronta in pochi secondi.
        </p>
        <Link
          to="/"
          data-testid="articolo-cta-verifica"
          className="inline-flex items-center justify-center bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold text-xl min-h-[56px] px-8 py-3 rounded-2xl shadow-md transition-all active:scale-95"
        >
          Verifica un messaggio gratis
        </Link>
      </div>

      {related.length > 0 && (
        <section className="mt-14">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-6">
            Leggi anche
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/guida/${r.slug}`}
                data-testid={`articolo-correlato-${r.slug}`}
                className="block bg-white p-6 rounded-3xl shadow-lg border-2 border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all"
              >
                <span className="text-sm font-bold text-[#16A34A]">{r.category}</span>
                <h3 className="font-heading text-lg md:text-xl font-bold text-[#1E3A8A] mt-2 leading-snug">
                  {r.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-14">
        <ShareSection variant="compact" />
      </div>
    </article>
  );
}
